import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/joy";
import Color from "color";
import { HTTPError } from "griptonite-api-client/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useShallow } from "zustand/react/shallow";

import { getAvailableLabelUuids } from "@/components/RouteLabels/routeLabelsConfig";
import { useRoutesUiStore } from "@/components/RoutesProvider";
import { useRouteLabelConfigQuery } from "@/hooks/routeLabels";

import { Route } from "../../core/types";
import {
  useBulkUpdateRoutesMutation,
  useCreateRouteMutation,
  useCurrentVenueCompetitionLookup,
  useRoutesByUuids,
  useUpdateRouteMutation,
} from "../../hooks/routes";
import {
  handleFormError,
  transformValidationErrorToErrorsArray,
} from "../../utils/handleFormError";
import FormErrorAlert from "../FormErrorAlert";
import { useModal } from "../modals/useModal";
import {
  PublishRouteFormValuesSchema,
  RouteBulkFormValues,
  RouteFormValues,
  bulkFormValuesToRoutePatches,
  routeToFormValues,
  routesToBulkFormValues,
} from "../RouteForm/formValues";
import { FormButtons, RouteFormMultiple, RouteFormSingle } from "../RouteForm/RouteFormFields";
import useSyncFormWithUiStore from "../RouteForm/useSyncFormWithUiStore";
import { RouteNameText } from "../RoutesTable/RouteName";
import RoutePin from "../RoutesTable/RoutePin";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";

// type Inputs = {
//   gradeValue: string | null;
//   gradeSystem: string;
//   number: number;
// };

const RouteHeader = ({
  route,
  routes,
}: {
  route?: Partial<Route> & { uuid: string };
  routes?: Route[];
}) => {
  const {
    routeFormEdits,
    routeFormIsDirty,
    setRouteFormIsDirty,
    setDisplayRouteSidebar,
    createRouteData,
    setCreateRouteData,
    clearMapPositionEdit,
    clearRouteFormEdit,
  } = useRoutesUiStore(
    useShallow((state) => ({
      routeFormEdits: state.routeFormEdits,
      routeFormIsDirty: state.routeFormIsDirty,
      setRouteFormIsDirty: state.setRouteFormIsDirty,
      setDisplayRouteSidebar: state.setDisplayRouteSidebar,
      createRouteData: state.createRouteData,
      setCreateRouteData: state.setCreateRouteData,
      clearMapPositionEdit: state.clearMapPositionEdit,
      clearRouteFormEdit: state.clearRouteFormEdit,
    })),
  );

  const { reset } = useFormContext();

  if (route) {
    route = { ...route, ...routeFormEdits[route.uuid] };
  }

  let bgColor: string;
  let fgColor: string;

  if (route) {
    const colors = (route.colors || "#FFFFFF").split(":");
    bgColor = colors[0];
    fgColor = Color(bgColor).isDark() ? "#FFFFFF" : "#000000";
  } else {
    bgColor = "#FFFFFF";
    fgColor = "#000000";
  }

  const [, setIsExiting] = useState(false);
  const { display: displayModal } = useModal();

  const onTryClose = () => {
    if (routeFormIsDirty || createRouteData) {
      displayModal("UNSAVED_ROUTE_CHANGES_PROMPT", { uuid: route?.uuid });
    } else {
      onClose();
    }
  };

  const onClose = () => {
    reset();
    setRouteFormIsDirty(false);
    setDisplayRouteSidebar(false);
    setIsExiting(false);
    if (createRouteData || route?.uuid) {
      clearMapPositionEdit(createRouteData ? "CREATING" : route!.uuid);
      clearRouteFormEdit(createRouteData ? "CREATING" : route!.uuid);
    } else if (routes) {
      routes?.forEach((route) => clearRouteFormEdit(route.uuid));
    }
    createRouteData && setCreateRouteData(undefined);
  };

  return (
    <Box
      sx={{
        height: 42,
        backgroundColor: bgColor,
        color: fgColor,
        padding: 1,
        fontSize: 16,
        fontWeight: "bold",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        border: "1px solid",
        borderColor: "#00000033",
        borderRadius: "16px 16px 0 0",
      }}
    >
      {!routes ? (
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: fgColor,
          }}
        >
          {route ? <RouteNameText route={route} /> : "?"}
        </Typography>
      ) : null}
      {routes ? (
        <Stack
          direction="row"
          spacing={routes.length > 10 ? -1.5 : 0.5}
          sx={{ overflow: "hidden" }}
        >
          {routes.map((route, _index) => (
            <Box sx={{ flexShrink: 0 }} key={route.uuid}>
              <RoutePin route={route} />
            </Box>
          ))}
        </Stack>
      ) : null}
      <IconButton
        size="sm"
        sx={{
          marginBottom: -0.5,
          borderRadius: 100,
          "&:hover": {
            backgroundColor: fgColor + "33",
          },
        }}
        onClick={onTryClose}
      >
        <Close htmlColor={fgColor} />
      </IconButton>
    </Box>
  );
};

const MultipleRouteDetail = ({ routes, hidden }: { routes: Route[]; hidden?: boolean }) => {
  const defaultValues = useMemo(() => routesToBulkFormValues(routes), [routes]);
  const routeUuids = useMemo(() => routes.map((route) => route.uuid), [routes]);

  const mutation = useBulkUpdateRoutesMutation();

  const methods = useForm<RouteBulkFormValues>({
    defaultValues,
    // resolver: zodResolver(RouteFormValuesSchema),
    mode: "onBlur",
  });
  useEffect(() => {
    methods.reset(routesToBulkFormValues(routes));
  }, [routes, methods]);

  const { displayRouteSidebar, clearRouteFormEdit } = useRoutesUiStore(
    useShallow((state) => ({
      displayRouteSidebar: state.displayRouteSidebar,
      clearRouteFormEdit: state.clearRouteFormEdit,
    })),
  );
  useEffect(() => {
    if (!displayRouteSidebar) {
      methods.reset();
    }
  }, [displayRouteSidebar, methods]);

  const onSubmit = useCallback(
    async (values: RouteBulkFormValues) => {
      const onlyChangedFields = Object.fromEntries(
        Object.entries(values).filter(
          ([key]) => methods.formState.dirtyFields[key as keyof RouteBulkFormValues],
        ),
      ) as Partial<RouteBulkFormValues>;
      console.log("Submitting patch:", onlyChangedFields);
      await mutation.mutateAsync(bulkFormValuesToRoutePatches(onlyChangedFields, routeUuids), {
        onError: async (error) => {
          handleFormError(error, methods);
        },
      });
    },
    [methods, mutation, routeUuids],
  );

  useSyncFormWithUiStore({ routes, methods });

  return (
    <Box
      sx={{
        height: "100%",
        display: hidden ? "none" : "flex",
        flexDirection: "column",
        borderTopLeftRadius: 16,
        overflow: "hidden",
      }}
    >
      <FormProvider {...methods}>
        <RouteHeader routes={routes} />
        <Stack
          spacing={1}
          sx={{
            fontSize: 14,
            flex: 1,
            paddingTop: 1,
            overflow: "auto",
            borderLeft: "1px solid",
            borderColor: "var(--joy-palette-neutral-300)",
            position: "relative",
          }}
          component="form"
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
          onReset={() => {
            methods.reset();
            routes.forEach((route) => {
              clearRouteFormEdit(route.uuid);
            });
          }}
        >
          <FormErrorAlert />
          <RouteFormMultiple routes={routes} />
          <FormButtons />
          {/* <DevTool control={methods.control} /> */}
        </Stack>
      </FormProvider>
    </Box>
  );
};

const SingleRouteDetail = ({
  route,
  hidden,
  isCreating,
}: {
  route?: Route | (Partial<Route> & { uuid: string });
  hidden?: boolean;
  isCreating: boolean;
}) => {
  const updateMutation = useUpdateRouteMutation(route?.uuid);
  const createMutation = useCreateRouteMutation();
  const mutation = isCreating ? createMutation : updateMutation;

  const {
    clearMapPositionEdit,
    clearRouteFormEdit,
    setCreateRouteData,
    setDisplayRouteSidebar,
    setRouteFormIsDirty,
  } = useRoutesUiStore(
    useShallow((state) => ({
      clearMapPositionEdit: state.clearMapPositionEdit,
      clearRouteFormEdit: state.clearRouteFormEdit,
      setCreateRouteData: state.setCreateRouteData,
      setDisplayRouteSidebar: state.setDisplayRouteSidebar,
      setRouteFormIsDirty: state.setRouteFormIsDirty,
    })),
  );

  // const mapPositionEdit = route?.uuid
  //   ? mapPositionEdits[route.uuid]
  //   : undefined;
  // const map = mapPositionEdit?.map || route?.map;
  // const mapPosition =
  //   mapPositionEdit?.x !== null &&
  //   mapPositionEdit?.y !== null &&
  //   mapPositionEdit?.x !== undefined &&
  //   mapPositionEdit?.y !== undefined
  //     ? { x: mapPositionEdit.x, y: mapPositionEdit.y }
  //     : route?.mapPosition;
  // const startLine = mapPositionEdit?.line
  //   ? mapPositionEdit.line
  //   : route?.startLine?.uuid;

  const defaultValues = useMemo(() => {
    const formValues = routeToFormValues(route);
    // if (map !== undefined) {
    //   formValues.map = map;
    // }
    // if (mapPosition !== undefined) {
    //   formValues.mapPosition = mapPosition;
    // }
    // if (startLine !== undefined) {
    //   formValues.startLine = startLine;
    // }
    return formValues;
  }, [route]);
  // }, [route, map, mapPosition, startLine]);

  const methods = useForm<RouteFormValues>({
    defaultValues,
    resolver: zodResolver(PublishRouteFormValuesSchema),
    mode: "onBlur",
  });

  useEffect(() => {
    methods.reset(routeToFormValues(route));
  }, [route, methods]);

  const displayRouteSidebar = useRoutesUiStore((s) => s.displayRouteSidebar);
  useEffect(() => {
    if (!displayRouteSidebar) {
      methods.reset();
    }
  }, [displayRouteSidebar, methods]);

  useSyncFormWithUiStore({ route, methods });

  const competitionUuid = methods.watch("competition");
  const competition = useCurrentVenueCompetitionLookup(competitionUuid ?? undefined);
  // The route groups sent on save come from the competition, so wait for it.
  const isCompetitionLoading = !!competitionUuid && !competition;
  const { data: labelConfig } = useRouteLabelConfigQuery();

  const onSubmit = async (
    values: RouteFormValues & {
      startTime?: string;
    },
  ) => {
    // This gets the server to set the current time as the start time
    if (isCreating) {
      values.startTime = "NOW";
    }

    // Set competition route groups on the payload.
    // If they're already set and match the current competition, keep them.
    // Otherwise, use ALL the form competition's route groups.
    const validRouteGroupUuids = values.competitionRouteGroups.filter((uuid) =>
      competition?.routeGroups.some((crg) => crg.uuid === uuid),
    );
    if (values.competition) {
      if (!competition) {
        return;
      }
      values.competitionRouteGroups = validRouteGroupUuids.length
        ? validRouteGroupUuids
        : competition.routeGroups.map((x) => x.uuid);
    } else {
      values.competitionRouteGroups = [];
    }
    const { competition: _, labels, ...rest } = { ...values };
    // The server only takes active labels, and keeps a route's archived ones
    // when they're left out. Without the config we can't tell them apart, so
    // the labels are left alone entirely.
    const availableLabels = labelConfig && getAvailableLabelUuids(labelConfig);
    const payload = availableLabels
      ? { ...rest, labels: labels.filter((uuid) => availableLabels.has(uuid)) }
      : rest;
    payload.height = payload.height ? Math.round(payload.height) : payload.height;

    await mutation.mutateAsync(payload, {
      onError: async (error) => {
        console.log({ ...error });
        if (error instanceof HTTPError && error.response.status === 400 && error.jsonData) {
          const errors = transformValidationErrorToErrorsArray(error);
          errors.forEach((error) => {
            // oxlint-disable-next-line typescript/no-explicit-any
            methods.setError(error.path as any, {
              type: error.code,
              message: error.message,
            });
          });
        }
      },
      onSuccess: () => {
        clearMapPositionEdit(isCreating ? "CREATING" : route!.uuid);
        clearRouteFormEdit(isCreating ? "CREATING" : route!.uuid);
        if (isCreating) {
          setCreateRouteData(undefined);
          methods.reset();
        }
        setRouteFormIsDirty(false);
        setDisplayRouteSidebar(false);
      },
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: hidden ? "none" : "flex",
        flexDirection: "column",
        borderTopLeftRadius: 16,
        overflow: "hidden",
      }}
    >
      <FormProvider {...methods}>
        <RouteHeader route={route} />
        <Stack
          spacing={1}
          sx={{
            fontSize: 14,
            flex: 1,
            paddingTop: 1,
            overflow: "auto",
            borderLeft: "1px solid",
            borderColor: "var(--joy-palette-neutral-300)",
            position: "relative",
          }}
          autoComplete="off"
          component="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          onReset={() => {
            methods.reset();
            if (route?.uuid) {
              clearMapPositionEdit(route.uuid);
              clearRouteFormEdit(route.uuid);
            }
          }}
        >
          <FormErrorAlert />
          <RouteFormSingle route={route} />
          <FormButtons disabled={isCompetitionLoading} />
          {/* <DevTool control={methods.control} /> */}
        </Stack>
      </FormProvider>
    </Box>
  );
};

export default function RouteDetailSidebar({ hidden }: { hidden?: boolean }) {
  const selectedRouteUuids = useRoutesTableStore((s) => s.selectedRouteUuids);
  const createRouteData = useRoutesUiStore((s) => s.createRouteData);

  const routes = useRoutesByUuids(selectedRouteUuids);

  if (routes.length === 1 || createRouteData) {
    return (
      <SingleRouteDetail
        isCreating={!!createRouteData}
        route={createRouteData || routes[0]}
        hidden={hidden}
      />
    );
  }
  if (routes.length === 0) {
    return null;
  }
  return <MultipleRouteDetail routes={routes} hidden={hidden} />;
}
